// COMENZILE DE NAVIGARE ALE UNEI VEDERI 3D, SCRISE O SINGURĂ DATĂ.
//
// Aceeași clădire se învârte pe mai multe pânze: desenul SVG al planului,
// macheta arhitecturii (2D și 3D) și modelul de structură. Omul trece între ele
// din butoane alăturate, nu intră în altă aplicație — deci ACELAȘI GEST TREBUIE
// SĂ FACĂ ACELAȘI LUCRU. Cât timp fiecare vedere își scria singură rotița și
// tastele, asta nu se putea apăra: modelul de structură a rămas cu comenzile
// din cutie ale `OrbitControls` — rotița mărea ORICE derulare, deci scroll-ul
// cu două degete pe trackpad apropia clădirea în loc s-o miște, și nicio tastă
// nu făcea nimic.
//
// Aici stau, o dată:
//
//   * ROTIȚA. Un singur tip de eveniment poartă trei gesturi, iar browserul nu
//     le etichetează: `scrollIntent` (v. `viewer/view.ts`) citește care e care.
//     Pinch și rotița mouse-ului MĂRESC, scroll-ul cu două degete MUTĂ.
//   * GESTURILE SAFARI. Safari nu trimite pinch-ul ca `wheel` cu `ctrlKey`, cum
//     fac Chrome și Firefox, ci `gesturestart`/`gesturechange` cu `scale` și
//     `rotation`. Fără ele, pe un Mac cu Safari vederea nu se poate nici mări,
//     nici răsuci cu trackpad-ul.
//   * TASTELE, ținute apăsat ca într-un joc: WASD/săgeți mută, Q/E rotesc,
//     R/F măresc, 0 readuce vederea la cadrul ei.
//
// Ce NU stă aici e cum se aplică o comandă pe o cameră anume — o ortografică se
// mărește strângând cadrul (`zoom`), una în perspectivă apropiindu-se. Aia e
// treaba `primitiveOrbita`, dedesubt; comenzile de mai sus nu știu ce cameră au
// sub ele.

import * as THREE from "three";
import type { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { scrollIntent } from "./viewer/view";

/** Ce știe să facă o vedere, indiferent de camera din spatele ei. */
export interface Comenzi3D {
  /** Mărește cu factorul `f` (>1 apropie). Cu ancoră — pixeli față de colțul
   *  stânga-sus al pânzei — punctul de sub cursor RĂMÂNE SUB CURSOR. */
  mareste(f: number, ancoraX?: number, ancoraY?: number): void;
  /** Mută CADRUL cu (dx, dy) metri pe axele ecranului: `dx > 0` duce camera la
   *  stânga, adică desenul la dreapta. */
  muta(dx: number, dy: number): void;
  /** Răsucește vederea cu `rad` în jurul verticalei. */
  roteste(rad: number): void;
  /** Cât înseamnă un pixel, în metri, la mărirea curentă. */
  peMetru(): number;
  /** Tasta 0: vederea se întoarce la cadrul ei. */
  reseteaza(): void;
  /** Chemat după fiecare comandă. Vederile care randează LA CERERE (nu într-o
   *  buclă continuă) randează aici; celelalte îl lasă nedefinit. */
  dupa?(): void;
}

// ---- CINE PRIMEȘTE TASTELE -------------------------------------------------
//
// Rotița și gesturile sunt legate de pânză, deci se adresează singure vederii
// de sub cursor. Tastele nu: ele ajung la fereastră. Cât timp exista o singură
// machetă pe ecran nu conta, dar pagina Pasului 7 poate ține modelul lângă
// deformata modală, iar un „W" ar fi mutat amândouă vederile deodată — două
// clădiri care fug într-o parte când vrei să miști una.
//
// Regula: tastele merg la vederea PE CARE OMUL A ATINS-O ULTIMA (a intrat cu
// mausul în ea sau a apăsat în ea). Dacă e una singură pe ecran, ea e mereu
// aceea — deci comportarea unei pagini cu o singură vedere nu se schimbă.
interface Instanta { el: HTMLElement; c: Comenzi3D }
const instante = new Set<Instanta>();
let activa: Instanta | null = null;
const tinta = (): Instanta | null =>
  (activa && instante.has(activa) ? activa : null)
  ?? (instante.size === 1 ? instante.values().next().value! : null);

const taste = new Set<string>();
// săgețile sunt același lucru cu WASD, nu o a doua schemă
const SEMN: Record<string, string> = {
  arrowleft: "a", arrowright: "d", arrowup: "w", arrowdown: "s",
};
const scrie = (t: EventTarget | null) => {
  const x = t as HTMLElement | null;
  return !!x && (/^(INPUT|TEXTAREA|SELECT)$/.test(x.tagName) || x.isContentEditable);
};

const PAS_PX = 9;        // pixeli pe cadru, ca pe desen
const PAS_ROT = 0.02;    // radiani pe cadru
const PAS_ZOOM = 1.02;

let cadru = 0;
function bucla() {
  cadru = 0;
  if (!taste.size) return;
  cadru = requestAnimationFrame(bucla);
  const inst = tinta();
  if (!inst) return;
  const c = inst.c;
  const pas = PAS_PX * c.peMetru();
  let dx = 0, dy = 0;
  // `muta` deplasează camera cu `-dx·dreapta - dy·sus`, deci ca privirea să
  // URCE la W trebuie `dy` NEGATIV. Sensul e apărat de `viewer/taste.test.ts`.
  if (taste.has("a")) dx += pas;      // cameră stânga
  if (taste.has("d")) dx -= pas;      // cameră dreapta
  if (taste.has("w")) dy -= pas;      // cameră sus
  if (taste.has("s")) dy += pas;      // cameră jos
  if (dx || dy) c.muta(dx, dy);
  const rot = (taste.has("q") ? 1 : 0) - (taste.has("e") ? 1 : 0);
  if (rot) c.roteste(rot * PAS_ROT);
  if (taste.has("r")) c.mareste(PAS_ZOOM);
  if (taste.has("f")) c.mareste(1 / PAS_ZOOM);
  c.dupa?.();
}

const jos = (e: KeyboardEvent) => {
  if (e.metaKey || e.ctrlKey || e.altKey || scrie(e.target)) return;
  const b = e.key.toLowerCase();
  const g = SEMN[b] ?? b;
  if (g.length === 1 && "wasdqerf".includes(g)) {
    if (!tinta()) return;
    e.preventDefault();
    taste.add(g);
    if (!cadru) cadru = requestAnimationFrame(bucla);
  } else if (b === "0") {
    const inst = tinta();
    if (!inst) return;
    e.preventDefault();
    inst.c.reseteaza();
    inst.c.dupa?.();
  }
};
const sus = (e: KeyboardEvent) => {
  taste.delete(SEMN[e.key.toLowerCase()] ?? e.key.toLowerCase());
};
// O fereastră care pierde focusul nu mai trimite `keyup`, iar tasta ar rămâne
// „apăsată" la nesfârșit.
const goleste = () => taste.clear();

function asculta(pornit: boolean) {
  if (pornit) {
    window.addEventListener("keydown", jos);
    window.addEventListener("keyup", sus);
    window.addEventListener("blur", goleste);
  } else {
    window.removeEventListener("keydown", jos);
    window.removeEventListener("keyup", sus);
    window.removeEventListener("blur", goleste);
  }
}

/** Leagă rotița, gesturile de trackpad și tastele de pânza `el`.
 *  Întoarce funcția care le desface — de chemat la demontarea vederii. */
export function legComenzi3D(el: HTMLElement, c: Comenzi3D): () => void {
  const inst: Instanta = { el, c };
  if (!instante.size) asculta(true);
  instante.add(inst);
  const revendica = () => { activa = inst; };
  el.addEventListener("pointerenter", revendica);
  el.addEventListener("pointerdown", revendica);

  // ---- cele două degete pe trackpad, în Safari ---------------------------
  // `preventDefault` pe `gesturestart` nu e opțional: fără el Safari mărește
  // PAGINA, deci gestul nu doar că nu face ce trebuie, face altceva vizibil.
  let gScara = 1, gUnghi = 0, gActiv = false;
  let gAnc = { x: 0, y: 0 };
  const G = (e: Event) => e as Event &
    { rotation: number; scale: number; clientX: number; clientY: number };
  const gestStart = (e: Event) => {
    e.preventDefault();
    const g = G(e), r = el.getBoundingClientRect();
    gScara = g.scale || 1;
    gUnghi = g.rotation;
    gAnc = { x: g.clientX - r.left, y: g.clientY - r.top };
    gActiv = true;
    activa = inst;
  };
  const gestChange = (e: Event) => {
    e.preventDefault();
    const g = G(e);
    const s = g.scale || 1;
    if (s > 0 && s !== gScara) { c.mareste(s / gScara, gAnc.x, gAnc.y); gScara = s; }
    if (g.rotation !== gUnghi) {
      c.roteste(THREE.MathUtils.degToRad(g.rotation - gUnghi));
      gUnghi = g.rotation;
    }
    c.dupa?.();
  };
  const gestEnd = (e: Event) => { e.preventDefault(); gActiv = false; };
  el.addEventListener("gesturestart", gestStart);
  el.addEventListener("gesturechange", gestChange);
  el.addEventListener("gestureend", gestEnd);

  const laRotita = (e: WheelEvent) => {
    // Fără asta browserul dă zoom pe pagină la pinch și pleacă înapoi în
    // istoric la scroll orizontal.
    e.preventDefault();
    // Cât ține un gest Safari, `wheel` se ignoră — pe un browser care le-ar
    // trimite pe amândouă, mărirea s-ar aplica de două ori.
    if (gActiv) return;
    activa = inst;
    const r = el.getBoundingClientRect();
    if (scrollIntent(e) === "zoom") {
      c.mareste(Math.exp(-e.deltaY * 0.0015), e.clientX - r.left, e.clientY - r.top);
    } else {
      const m = c.peMetru();
      c.muta(-e.deltaX * m, e.deltaY * m);
    }
    c.dupa?.();
  };
  el.addEventListener("wheel", laRotita, { passive: false });

  return () => {
    el.removeEventListener("pointerenter", revendica);
    el.removeEventListener("pointerdown", revendica);
    el.removeEventListener("gesturestart", gestStart);
    el.removeEventListener("gesturechange", gestChange);
    el.removeEventListener("gestureend", gestEnd);
    el.removeEventListener("wheel", laRotita);
    instante.delete(inst);
    if (activa === inst) activa = null;
    if (!instante.size) { asculta(false); taste.clear(); }
  };
}

/** SCHEMA DE MOUSE, aceeași pe toate machetele.
 *
 *  `OrbitControls` vine cu alta — stânga ROTEȘTE mereu, rotița mărește orice
 *  derulare. Pe o vedere PLANĂ (de sus) rotirea n-are ce căuta: o tragere ar
 *  scoate vederea din plan, adică ai pierde exact ce ai cerut. Pe o machetă
 *  rotirea trebuie să fie pe butonul STÂNG, altfel pe un trackpad de laptop
 *  clădirea nu se poate întoarce deloc.
 *
 *  Mărirea NU trece prin `OrbitControls` (`enableZoom = false`): ea se citește
 *  în `legComenzi3D`, unde scroll-ul cu două degete mută în loc să mărească. */
export function schemaMouse(ctrl: OrbitControls, spatial: boolean) {
  ctrl.enableRotate = spatial;
  ctrl.mouseButtons = {
    LEFT: spatial ? THREE.MOUSE.ROTATE : THREE.MOUSE.PAN,
    MIDDLE: THREE.MOUSE.DOLLY,
    RIGHT: THREE.MOUSE.ROTATE,
  };
  // Pe atingere, două degete: pe machetă rotesc, pe plan mută. `DOLLY` nu are
  // ce căuta în listă — mărirea trece prin rotiță, ca pe desen.
  ctrl.touches = {
    ONE: THREE.TOUCH.PAN,
    TWO: spatial ? THREE.TOUCH.ROTATE : THREE.TOUCH.PAN,
  };
  ctrl.enableZoom = false;
}

/** Comenzile de mai sus, aplicate pe o cameră (ortografică sau în perspectivă)
 *  cu `OrbitControls`. Aici, și numai aici, se știe ce fel de cameră e. */
export function primitiveOrbita(
  camera: THREE.OrthographicCamera | THREE.PerspectiveCamera,
  ctrl: OrbitControls,
  el: HTMLElement,
  extra: { reseteaza: () => void; dupa?: () => void },
): Comenzi3D {
  const orto = (camera as THREE.OrthographicCamera).isOrthographicCamera === true;
  const ZOOM_MIN = 0.05, ZOOM_MAX = 60;

  /** Mută cadrul cu (dx, dy) în METRI, pe axele ecranului. Se deplasează ȘI
   *  camera, ȘI ținta, ca orbitarea să rămână în jurul a ce se vede. */
  const muta = (dx: number, dy: number) => {
    const dreapta = new THREE.Vector3().setFromMatrixColumn(camera.matrix, 0);
    const susV = new THREE.Vector3().setFromMatrixColumn(camera.matrix, 1);
    const d = dreapta.multiplyScalar(-dx).add(susV.multiplyScalar(-dy));
    camera.position.add(d);
    ctrl.target.add(d);
  };

  const peMetru = () => {
    const h = el.clientHeight || 1;
    if (orto) {
      const c = camera as THREE.OrthographicCamera;
      return (c.top - c.bottom) / c.zoom / h;
    }
    const c = camera as THREE.PerspectiveCamera;
    const d = camera.position.distanceTo(ctrl.target);
    return (2 * d * Math.tan(THREE.MathUtils.degToRad(c.fov / 2))) / h;
  };

  const mareste = (f: number, ancoraX?: number, ancoraY?: number) => {
    if (!(f > 0) || f === 1) return;
    const inainte = peMetru();
    if (orto) {
      const c = camera as THREE.OrthographicCamera;
      c.zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, c.zoom * f));
      c.updateProjectionMatrix();
    } else {
      // În perspectivă nu se strânge cadrul, se apropie camera. Limitele sunt
      // ale `OrbitControls`, ca tragerea cu butonul din mijloc și rotița să nu
      // se oprească în locuri diferite.
      const spre = camera.position.clone().sub(ctrl.target);
      const d = spre.length();
      if (!(d > 0)) return;      // camera fix în țintă: n-are pe ce direcție
      const nou = Math.max(ctrl.minDistance || 1e-3,
                           Math.min(ctrl.maxDistance || Infinity, d / f));
      if (nou === d) return;
      camera.position.copy(ctrl.target).addScaledVector(spre.divideScalar(d), nou);
      // Depărtarea trebuie să încapă în frustum: `far` s-a ales pentru cadrul
      // de la deschidere, iar cine se trage îndărăt de câteva ori ar vedea
      // clădirea tăiată de planul din spate. Crește, niciodată nu scade — o
      // scădere ar tăia tocmai ce se vedea până acum.
      const c = camera as THREE.PerspectiveCamera;
      if (nou * 5 > c.far) { c.far = nou * 5; c.updateProjectionMatrix(); }
    }
    const dupaM = peMetru();
    if (dupaM === inainte) return;
    // MĂRIREA SE FACE SPRE CURSOR: punctul de sub el rămâne pe loc. Fără asta,
    // lucrul la care te uiți fuge din cadru la fiecare rotiță. Punctul stă la
    // `ox` metri de centrul ecranului; după mărire aceiași pixeli înseamnă
    // `ox·(dupa/inainte)` metri, deci cadrul trebuie dus cu diferența.
    if (ancoraX == null || ancoraY == null) return;
    const w = el.clientWidth, h = el.clientHeight;
    if (!w || !h) return;
    const ox = (ancoraX - w / 2) * inainte;
    const oy = -(ancoraY - h / 2) * inainte;
    const k = 1 - dupaM / inainte;
    // camera se apropie DE punct, iar `muta` mișcă CADRUL: de aici minusul
    muta(-ox * k, -oy * k);
  };

  /** Pe machetă se învârte camera în jurul țintei (azimut); pe vederea plană
   *  orbitarea e stinsă, deci se rotește `up` — adică ecranul. */
  const roteste = (rad: number) => {
    if (!rad) return;
    const z = new THREE.Vector3(0, 0, 1);
    if (ctrl.enableRotate) {
      const raza = camera.position.clone().sub(ctrl.target).applyAxisAngle(z, rad);
      camera.position.copy(ctrl.target).add(raza);
    } else {
      camera.up.applyAxisAngle(z, rad);
    }
    camera.lookAt(ctrl.target);
  };

  return { mareste, muta, roteste, peMetru, ...extra };
}
